// src/features/task/create/scope.ts
// The parent a task is created under, and what that parent implies.
//
// The parent is not decoration: `ResolveTaskContext` resolves allowed types,
// templates, categories, numbering and the effective policy by walking the
// scope chain from the parent outwards. Two projects may each own a template
// called "Bug report" — same name, different origin — so the chain and the
// origin of each entry both have to be modelled explicitly.

import type { Entity } from '@core/types/entity';
import type { TaskTemplate } from '@core/types/task';
import { makeEntity, type ScopeLike } from '@/features/task/panelHelpers';

/**
 * Parent kinds the picker offers.
 *
 * `none` is the default and a real answer, not an absence: the task is created
 * with no `parent` at all. It is deliberately first — a task belonging to
 * nothing is the common case, and assuming the tenant instead would file every
 * task under a container nobody chose.
 *
 * `project` and `team` have real pickers; `tenant` means the task genuinely
 * hangs off the tenant (an explicit choice, and then it *is* sent as the
 * parent); `entity` is the escape hatch for any other scope (department,
 * category, a custom container) named by type and id.
 */
export const PARENT_KINDS = ['none', 'project', 'team', 'tenant', 'entity'] as const;
export type ParentKind = (typeof PARENT_KINDS)[number];

/**
 * The entity that goes into `CreateTaskRequest.parent`, or null for "no parent".
 *
 * This is the distinction that must stay real in the payload: "no parent" sends
 * nothing, while "the whole tenant" sends the tenant entity. Both resolve their
 * types, templates and numbering against the tenant scope (see
 * `buildScopeChain`), but only one of them files the task under something.
 */
export function parentEntityFor(
  scope: ScopeLike,
  kind: ParentKind,
  id: string,
  typeName: ParentType,
): Entity | null {
  if (kind === 'none') return null;
  if (kind === 'tenant') return makeEntity(scope.tenantId, 'tenant', scope);
  if (!id.trim() || !typeName.trim()) return null;
  return makeEntity(id.trim(), typeName.trim(), scope);
}

/**
 * The workspace scope, as the task workspace models it. Only a project or a
 * team is a container a task can hang off; "my tasks" and "everything" are ways
 * of looking, not places to file into.
 */
export interface QuickCreateScope {
  scopeType: string;
  scopeEntity: { id: string; type: string; name: string } | null;
}

/**
 * The parent a task filed from a scoped list belongs under: whatever that list
 * is scoped to. Anything else — personal, tenant-wide, an ad-hoc entity scope —
 * means no parent at all. The tenant is never a silent stand-in: filing every
 * task under a container nobody chose is the bug the full page just fixed.
 */
export function parentForScope(scope: QuickCreateScope | null | undefined): {
  kind: ParentKind;
  id: string;
} {
  const id = scope?.scopeEntity?.id ?? '';
  if (!id) return { kind: 'none', id: '' };
  if (scope?.scopeType === 'project') return { kind: 'project', id };
  if (scope?.scopeType === 'team') return { kind: 'team', id };
  return { kind: 'none', id: '' };
}

/** The entity type name sent to the backend — a kind, or a free-form type. */
export type ParentType = string;

/** A parent candidate reduced to what the picker and the chain need. */
export interface ParentOption {
  id: string;
  label: string;
  /** The record's own parent, when it exposes one — the next chain link. */
  parent?: Entity;
}

/**
 * Build the scope chain, most-specific → least-specific.
 *
 * Only links that can actually be established are sent: the chosen parent, the
 * parent's own parent when the loaded record exposes one, and the tenant. A
 * fabricated link would make the server resolve a policy that does not exist;
 * a single-element chain would silently drop everything inherited.
 */
export function buildScopeChain(
  scope: ScopeLike,
  parent: { id: string; typeName: ParentType; parent?: Entity } | null,
): { scopeEntity: Entity; chain: Entity[] } {
  const tenant = makeEntity(scope.tenantId, 'tenant', scope);
  const chain: Entity[] = [];
  const seen = new Set<string>();

  const push = (entity: Entity | undefined) => {
    if (!entity?.id || seen.has(entity.id)) return;
    seen.add(entity.id);
    chain.push(entity);
  };

  if (parent?.id && parent.typeName !== 'tenant') {
    push(makeEntity(parent.id, parent.typeName, scope));
    if (parent.parent?.id) {
      push({ ...parent.parent, type_name: parent.parent.type_name || '' });
    }
  }
  push(tenant);

  return { scopeEntity: chain[0] ?? tenant, chain };
}

// ---------------------------------------------------------------------------
// Template origin
// ---------------------------------------------------------------------------

export type TemplateOriginKind =
  /** Bound to the exact parent being created under. */
  | 'parent'
  /** Bound to an ancestor of that parent (team, department, tenant). */
  | 'inherited'
  /** Bound to a kind of entity rather than one instance ("all projects"). */
  | 'entityType'
  /** Bound to nothing — offered everywhere. */
  | 'universal';

export interface TemplateOrigin {
  kind: TemplateOriginKind;
  /** The entity the template hangs off, when there is one. */
  scopeId?: string;
  /** The entity type it is bound to, when it is bound by type. */
  scopeType?: string;
}

/**
 * Where a template comes from, relative to the parent being created under.
 * `chainIds` is the resolved scope chain in order; anything scoped to a chain
 * entry other than the first is inherited.
 */
export function templateOrigin(
  template: TaskTemplate,
  parentId: string,
  chainIds: string[],
): TemplateOrigin {
  const scopeId = template.scope_entity?.id || '';
  const scopeType = template.scope_entity_type || template.scope_entity?.type_name || '';

  if (scopeId && scopeId === parentId) return { kind: 'parent', scopeId, scopeType };
  if (scopeId && chainIds.includes(scopeId)) return { kind: 'inherited', scopeId, scopeType };
  if (scopeId) return { kind: 'inherited', scopeId, scopeType };
  if (scopeType) return { kind: 'entityType', scopeType };
  return { kind: 'universal' };
}

/**
 * The task type a template is bound to.
 *
 * `TaskTemplate.task_type_id` is being added by the backend (bd framework-w92i)
 * so a template can belong to a custom task type. Reading it defensively means
 * this compiles both before and after the regenerated types land.
 */
export function templateTaskTypeId(template: TaskTemplate): string {
  return (template as TaskTemplate & { task_type_id?: string }).task_type_id || '';
}

export interface TemplateEntry {
  template: TaskTemplate;
  id: string;
  name: string;
  origin: TemplateOrigin;
  /** Set when name + origin alone would not tell two entries apart. */
  disambiguator?: string;
}

/**
 * Decorate templates for rendering, preserving the server's order (which is
 * most-specific-first — closest scope wins). Nothing is filtered or re-sorted
 * here: `ResolveTaskContext` already scoped the list to the parent chain.
 *
 * Two entries that would render identically get a disambiguator, because a
 * person seeing "Bug report" twice with no difference cannot choose.
 */
export function decorateTemplates(
  templates: TaskTemplate[],
  parentId: string,
  chainIds: string[],
): TemplateEntry[] {
  const entries: TemplateEntry[] = templates.map((template) => ({
    template,
    id: template.entity?.id || '',
    name: template.name,
    origin: templateOrigin(template, parentId, chainIds),
  }));

  const counts = new Map<string, number>();
  for (const entry of entries) {
    const signature = `${entry.name}\u0000${entry.origin.kind}\u0000${entry.origin.scopeId ?? ''}`;
    counts.set(signature, (counts.get(signature) ?? 0) + 1);
  }
  for (const entry of entries) {
    const signature = `${entry.name}\u0000${entry.origin.kind}\u0000${entry.origin.scopeId ?? ''}`;
    if ((counts.get(signature) ?? 0) > 1 && entry.id) {
      entry.disambiguator = entry.id.slice(0, 8);
    }
  }
  return entries;
}

/** i18n key for an origin badge. */
export function templateOriginLabelKey(origin: TemplateOrigin): string {
  switch (origin.kind) {
    case 'parent':
      return 'task.createForm.origin.parent';
    case 'inherited':
      return 'task.createForm.origin.inherited';
    case 'entityType':
      return 'task.createForm.origin.entityType';
    default:
      return 'task.createForm.origin.universal';
  }
}
